import { V2Position, V2Vault } from '@/core/migrate';
import { MIGRATOR_ZAP } from '@/domain/migrate';
import UniswapV2Pair from '@/domain/migrate/abis/UniswapV2Pair';
import { signTypedData } from '@wagmi/core';
import {
  Provider,
  getChainConstant,
  getContract,
  increaseByPercentage,
} from 'nftx.js';
import { splitSignature } from 'ethers/lib/utils.js';

const signRemoveLiquidity = async ({
  vault,
  position,
  network,
  provider,
  slippage,
  v3VaultId,
}: {
  vault: Pick<V2Vault, 'lpStakingPool'>;
  position: Pick<V2Position, 'migrateOptions' | 'userAddress' | 'slp'>;
  provider: Provider;
  network: number;
  slippage: number;
  v3VaultId: string;
}): Promise<{
  signature: string;
  v: number;
  r: string;
  s: string;
  deadline: bigint;
}> => {
  const stakingTokenContract = getContract({
    address: vault.lpStakingPool.stakingToken.id,
    abi: UniswapV2Pair,
    provider,
  });
  const nonce = await stakingTokenContract.read.nonces({
    args: [position.userAddress],
  });
  const domain = {
    name: 'SushiSwap LP Token',
    version: '1',
    chainId: network,
    verifyingContract: vault.lpStakingPool.stakingToken.id,
  };
  const types = {
    Permit: [
      {
        name: 'owner',
        type: 'address',
      },
      {
        name: 'spender',
        type: 'address',
      },
      {
        name: 'value',
        type: 'uint256',
      },
      {
        name: 'nonce',
        type: 'uint256',
      },
      {
        name: 'deadline',
        type: 'uint256',
      },
    ],
  };
  const migrateOption = position.migrateOptions.find(
    (o) => o.vaultId === v3VaultId
  );
  if (!migrateOption) {
    throw new Error(`Not a valid vaultId for this position`);
  }
  // Add slippage to the slp amount - although if slippage adds more than the total amount then it will fail
  let slpToRemove = increaseByPercentage(migrateOption.maxSlp, slippage);
  if (slpToRemove > position.slp) {
    slpToRemove = position.slp;
  }

  const value = {
    owner: position.userAddress,
    spender: getChainConstant(MIGRATOR_ZAP, network),
    value: slpToRemove,
    nonce,
    deadline:
      108555083659983933209597798445644913612440610624038028786991485007418559037440n,
  };

  console.debug({
    domain,
    types,
    message: value,
    primaryType: 'Permit',
  });

  const signature = await signTypedData({
    domain,
    types,
    message: value,
    primaryType: 'Permit',
  });

  const { v, r, s } = splitSignature(signature);

  return {
    signature,
    v,
    r,
    s,
    deadline: value.deadline,
  };
};

export default signRemoveLiquidity;
